import React, { useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, IndianRupee, BookOpen, Map as MapIcon, Users, MapPin, X, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "./common";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg } from "../lib/api";
import { FieldActivityTable, EMPTY_AREA, areaParams, areaLabel, areaActive } from "./FieldActivityTable";
import { SyncHealthCard } from "./SyncHealthCard";
import { TerritoryFilterSelects, effectiveWardIds, splitIds, useTerritoryMasters } from "./TerritoryFilter";
import { useStickyOffset } from "../hooks/use-sticky-offset";

const REPORT_META = {
  collection: { icon: IndianRupee, desc: "Receipt-wise collection scroll with payment mode and collector. Filter by date range." },
  "demand-register": { icon: BookOpen, desc: "Property-wise total demand, paid and balance with zone/ward details." },
  "ward-summary": { icon: MapIcon, desc: "Ward-wise demand vs collection with collection percentage." },
  "collector-performance": { icon: Users, desc: "Collector-wise assignments, visits, receipts, amount, reassessments and surveys." },
};

/**
 * MIS Reports body shared by the Admin page and (Phase 143) the zone-scoped Supervisor page.
 *  - `lockedZoneIds`  : supervisor's mapped zone(s) — the Zone select is restricted (one zone -> fixed & disabled)
 *  - `canDownload`    : false for "View only" supervisors -> PDF / Excel buttons and the field-activity export are hidden
 *  - `ids`            : data-testid overrides so the admin page keeps its historical ids
 *  Phase 183: the Area filter is Zone → Supervisor → Ward → Mohalla. The Supervisor level lists the supervisors mapped to the
 *  chosen zone(s) (all of them when no zone is chosen) plus one "Unassigned (Zone Placeholder) — GESIL-<ZONE>" entry per zone
 *  that has wards no supervisor owns; selecting supervisors narrows the ward / mohalla options, the collector list and every
 *  figure to those supervisors' wards.
 */
export const MisReportsView = ({
  code, title = "MIS Reports", subtitle = "Download management reports as PDF or Excel",
  canDownload = true, lockedZoneIds = null, scopeZoneNames = [], expandable = true, collectionsHref,
  showFieldActivity = true, canExportFieldActivity = canDownload,   // Phase 152: "reports.field_activity" sub-feature
  hideReportCards = false,                                           // Phase 152: "reports.mis_reports" = No access
  ids = {},
}) => {
  const T = {
    stickyWrap: "reports-area-filter-sticky", filterCard: "reports-area-filter-card", areaPrefix: "reports-area",
    grid: "reports-grid", fieldSection: "admin-field-activity-section", fieldPrefix: "admin-field-activity",
    syncSection: "admin-sync-health-section", syncPrefix: "admin-sync-health", ...ids,
  };
  const locked = Array.isArray(lockedZoneIds) && lockedZoneIds.length > 0;
  const baseArea = useMemo(() => (locked && lockedZoneIds.length === 1 ? { ...EMPTY_AREA, zoneId: lockedZoneIds[0] } : EMPTY_AREA),
    [locked, lockedZoneIds]);

  const [reports, setReports] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busy, setBusy] = useState(null);
  // Phase 127: one Zone -> Ward -> Mohalla filter for every report, download and the field-activity table below
  const [area, setAreaRaw] = useState(baseArea);
  useEffect(() => { setAreaRaw(baseArea); }, [baseArea]);
  // a single locked zone can never be cleared back to "All zones" (the table's own "Clear area" included)
  const setArea = (next) => setAreaRaw(locked && lockedZoneIds.length === 1 ? { ...EMPTY_AREA, ...(next || {}), zoneId: lockedZoneIds[0] } : { ...EMPTY_AREA, ...(next || {}) });
  const masters = useTerritoryMasters(code, { withSupervisors: true });   // Phase 183: + Supervisor filter options
  const label = areaLabel(masters, area);
  const active = areaActive(area);
  const nSup = splitIds(area.supervisorId).length;
  const supNames = useMemo(() => splitIds(area.supervisorId).map((k) => (masters.supervisors || []).find((o) => o.key === k)?.name || "Supervisor"), [area.supervisorId, masters.supervisors]);
  const narrowed = locked ? !!(area.supervisorId || area.wardId || area.mohallaId || (lockedZoneIds.length > 1 && area.zoneId)) : active;
  // Phase 183: pages that only understand zone / ward (Collections register link) follow the supervisor choice via its wards
  const withEffectiveWards = (a) => {
    const eff = effectiveWardIds(masters, a, lockedZoneIds);
    return eff ? { ...a, wardId: eff } : a;
  };
  // Phase 128: the Area filter card sticks right under the app header while the report cards scroll
  const headerH = useStickyOffset();

  useEffect(() => {
    setLoadError(null);
    if (hideReportCards) { setReports([]); return; }
    api.get(`/t/${code}/reports`).then((r) => setReports(r.data)).catch((e) => { setLoadError(errMsg(e)); toast.error(errMsg(e)); });
  }, [code, hideReportCards]);

  const download = async (key, format) => {
    setBusy(`${key}-${format}`);
    try {
      const params = { format, ...areaParams(area) };
      if (key === "collection") {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const r = await api.get(`/t/${code}/reports/${key}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      // Prefer the server filename - it carries the area slug (e.g. ..._zone-1_ward-12.pdf)
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      a.download = (m && m[1]) || `${code}_${key}_${new Date().toISOString().slice(0, 10)}.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(label ? `Report downloaded for ${label}` : "Report downloaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  if (loadError && !reports) {
    return (
      <div>
        <PageHeader title={title} subtitle={subtitle} />
        <Card className="shadow-sm"><CardContent className="p-6 text-sm text-destructive" role="alert" data-testid="reports-load-error">{loadError}</CardContent></Card>
      </div>
    );
  }
  if (!reports) return <Spinner label="Loading reports..." />;

  const zoneNames = scopeZoneNames.filter(Boolean).join(", ");

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />

      <div className="md:sticky z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-3 mb-3 bg-background/95 supports-[backdrop-filter]:bg-background/80 backdrop-blur"
        style={{ top: headerH }} data-testid={T.stickyWrap}>
        <Card className="shadow-sm" data-testid={T.filterCard}>
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-end gap-3">
              <div className="lg:w-56 shrink-0">
                <p className="text-sm font-semibold font-heading flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-primary" /> Area filter
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {locked ? "Zone locked to your mapping — narrow by supervisor, ward or mohalla." : "Applies to every report, download and the field activity table below."}
                </p>
              </div>
              <div className="flex-1">
                <TerritoryFilterSelects masters={masters} value={area} onChange={setArea} testIdPrefix={T.areaPrefix} lockedZoneIds={lockedZoneIds}
                  levels={["zone", "supervisor", "ward", "mohalla"]} />
              </div>
              <Button variant="outline" className="h-10" onClick={() => setArea(baseArea)} disabled={!narrowed}
                data-testid={`${T.areaPrefix}-clear`}>
                <X className="h-4 w-4 mr-1.5" /> Clear
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid={`${T.areaPrefix}-status`}>
              {locked && (
                <Badge variant="outline" className="gap-1 font-normal bg-primary/10 text-primary border-primary/20" data-testid={`${T.areaPrefix}-scope-badge`}>
                  <Lock className="h-3 w-3" /> Your zone{scopeZoneNames.length > 1 ? "s" : ""}: {zoneNames || "mapped zone"}
                </Badge>
              )}
              {active ? (
                <>
                  <Badge variant="outline" className="gap-1 font-normal bg-accent text-accent-foreground border-transparent"
                    data-testid={`${T.areaPrefix}-badge`}>
                    <MapPin className="h-3 w-3" /> {label}
                  </Badge>
                  {nSup > 0 ? (
                    <span data-testid={`${T.areaPrefix}-supervisor-note`}>
                      Supervisor filter: reports cover the wards of {nSup === 1 ? supNames[0] : `${nSup} supervisors`}
                      {area.wardId || area.mohallaId ? " (narrowed further by the ward / mohalla picks)" : ""}; field activity lists only the collectors mapped to those wards.
                    </span>
                  ) : (
                    <span>Reports include only properties located in this area; field activity lists only collectors mapped to it.</span>
                  )}
                </>
              ) : locked ? (
                <span data-testid={`${T.areaPrefix}-all`}>Showing everything inside your mapped zones. Pick a zone, supervisor, ward or mohalla to narrow every report.</span>
              ) : (
                <span data-testid={`${T.areaPrefix}-all`}>Showing the whole municipality. Pick a zone, supervisor, ward or mohalla to narrow every report.</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {!canDownload && !hideReportCards && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="reports-view-only-note">
          <Eye className="h-3.5 w-3.5 shrink-0" />
          <span>Your MIS Reports access is <span className="font-medium text-foreground">View only</span> — every report is visible on screen; PDF / Excel downloads need Full access from your administrator.</span>
        </div>
      )}

      {hideReportCards ? (
        <div className="rounded-lg border bg-muted/30 p-6 text-center text-sm text-muted-foreground" data-testid="reports-cards-locked">
          <Lock className="h-5 w-5 mx-auto mb-2 opacity-70" /> MIS report cards are not available for your account.
        </div>
      ) : (
      <div className="grid gap-4 sm:grid-cols-2" data-testid={T.grid}>
        {reports.map((rep) => {
          const meta = REPORT_META[rep.key] || { icon: FileText, desc: "" };
          const Icon = meta.icon;
          return (
            <Card key={rep.key} className="shadow-sm" data-testid={`report-card-${rep.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">
                    <Icon className="h-4 w-4 text-accent-foreground" />
                  </div>
                  <span className="flex-1">{rep.title}</span>
                  {active && (
                    <Badge variant="outline" className="gap-1 font-normal text-[11px] bg-accent text-accent-foreground border-transparent"
                      data-testid={`report-card-area-${rep.key}`}>
                      <MapPin className="h-3 w-3" /> {label}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>{meta.desc}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {rep.supports_date_range && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">From date</Label>
                      <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">To date</Label>
                      <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
                    </div>
                  </div>
                )}
                {canDownload ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => download(rep.key, "pdf")} disabled={busy === `${rep.key}-pdf`}
                      data-testid={`download-pdf-${rep.key}`}>
                      <FileText className="h-3.5 w-3.5 mr-1.5" /> {busy === `${rep.key}-pdf` ? "Preparing..." : "PDF"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => download(rep.key, "xlsx")} disabled={busy === `${rep.key}-xlsx`}
                      data-testid={`download-xlsx-${rep.key}`}>
                      <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> {busy === `${rep.key}-xlsx` ? "Preparing..." : "Excel"}
                    </Button>
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`report-downloads-locked-${rep.key}`}>
                    <Lock className="h-3 w-3" /> Downloads need Full access
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {showFieldActivity && <>
      <div className="mt-8" data-testid={T.fieldSection}>
        <h2 className="text-base font-semibold font-heading mb-1">Daily field activity</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Per-collector visits, collections, new surveys and reassessments for any date range
          {active ? ` — filtered to ${label}, listing only collectors mapped there` : ""}. Click a column heading to sort
          {expandable ? "; click a collector's name for the ward → mohalla → transaction breakup" : ""}.
        </p>
        <FieldActivityTable code={code} testIdPrefix={T.fieldPrefix} area={area} onAreaChange={setArea} expandable={expandable} masters={masters}
          canExport={canExportFieldActivity} collectionsHref={collectionsHref ? (ap, a) => collectionsHref(ap, withEffectiveWards(a)) : undefined} />
      </div>

      <div className="mt-8" data-testid={T.syncSection}>
        <SyncHealthCard code={code} testIdPrefix={T.syncPrefix} collapsible defaultOpen={false} />
      </div>
      </>}
    </div>
  );
};
